/**
 * + New Mailbox on Settings › Email › Mailboxes (piece 32; T3803; decision
 * 12640): a super admin registers a Department or a Module mailbox the host
 * already serves. The window's fields, the options it offers and the checks a
 * value meets, pure, so the window and the route refuse in the same words.
 */
import { MAIL_SETTINGS_MAILBOXES_PATH } from './client';
import { isMailboxModuleKey, type MailboxHostModeValue } from './model';
import { MAILBOX_QUOTA_MAX_MEGABYTES } from './grants';
import { isEmailAddress } from '../validators';
import type { Module } from '../permissions/modules';

/** The window's options, read when it opens; the save posts to the list's own path. */
export const MAIL_SETTINGS_NEW_MAILBOX_PATH = `${MAIL_SETTINGS_MAILBOXES_PATH}/new`;

/** The two kinds the window makes; a Personal mailbox follows its person and is never made here. */
export const NEW_MAILBOX_KINDS = ['Department', 'Module'] as const;
export type NewMailboxKind = (typeof NEW_MAILBOX_KINDS)[number];

/** 🔒 The module whose mailbox may be the Documents Inbox's master (decisions 0251, 1621, 12640). */
export const DOCUMENTS_INBOX_MODULE: Module = 'Accounting';

/** One registered domain the address may sit on, and whether its newest Check Records passed SPF and DMARC. */
export interface NewMailboxDomain {
  domain: string;
  verified: boolean;
}

export interface NewMailboxOptions {
  domains: NewMailboxDomain[];
  departments: { id: string; name: string }[];
  modules: Module[];
  /** The address `accounting.inboxAddress` holds now, lower-cased; null while it is unset. */
  documentsInboxAddress: string | null;
}

/** What the create answers: the new row's id, which the list reloads to draw, and a sentence where the department's head took no grant (T3824, T3847). */
export interface NewMailboxAnswer {
  id: string;
  notice: string | null;
}

export interface NewMailboxInput {
  kind: NewMailboxKind;
  name: string;
  /** The part before the at sign. */
  localPart: string;
  domain: string;
  departmentId?: string | null;
  moduleKey?: string | null;
  hostMode: MailboxHostModeValue;
  /** Whole megabytes; null or absent for none. */
  quotaMegabytes?: number | null;
  /** Ticked only on a Module mailbox keyed to Accounting. */
  documentsInbox?: boolean;
}

/** Whether the window offers Make this the Documents Inbox for this kind and module. */
export function offersDocumentsInbox(kind: string, moduleKey: string | null | undefined): boolean {
  return kind === 'Module' && moduleKey === DOCUMENTS_INBOX_MODULE;
}

/** The address the window makes from its two parts, as a mailbox stores it. */
export function newMailboxAddress(localPart: string, domain: string): string {
  return `${localPart.trim()}@${domain.trim()}`.toLowerCase();
}

/** 🔨 What is wrong with a new mailbox, in a sentence, or null; the domains are the registered ones the window offered. */
export function newMailboxProblem(input: Partial<NewMailboxInput>, domains: readonly NewMailboxDomain[]): string | null {
  if (input.kind !== 'Department' && input.kind !== 'Module') return 'Choose whether this is a department’s mailbox or a module’s.';
  if (!input.name || !input.name.trim()) return 'A mailbox has a name the folder tree prints.';
  if (input.name.trim().length > 120) return 'A mailbox name is at most 120 characters.';
  if (!input.localPart || !input.localPart.trim() || input.localPart.includes('@')) return 'Type the part of the address before the at sign.';
  const domain = (input.domain ?? '').trim().toLowerCase();
  const registered = domains.find((d) => d.domain === domain);
  if (!registered) return 'Choose one of the organization’s registered domains.';
  if (!registered.verified) return `The domain ${domain} has not passed Check Records yet. Publish its SPF and DMARC records and press Check Records on Email domains first.`;
  if (!isEmailAddress(newMailboxAddress(input.localPart, domain))) return 'The address is not one email address.';
  if (input.kind === 'Department' && !input.departmentId) return 'A department’s mailbox names its department.';
  if (input.kind === 'Module' && !(input.moduleKey && isMailboxModuleKey(input.moduleKey))) return 'A module’s mailbox names its module.';
  if (input.hostMode !== 'WatchOnly' && input.hostMode !== 'Full') return 'Choose Watching Only or Full Access.';
  if (input.quotaMegabytes !== undefined && input.quotaMegabytes !== null && (!Number.isInteger(input.quotaMegabytes) || input.quotaMegabytes < 1 || input.quotaMegabytes > MAILBOX_QUOTA_MAX_MEGABYTES)) {
    return `A quota is a whole number of megabytes from 1 to ${MAILBOX_QUOTA_MAX_MEGABYTES}, or empty for none.`;
  }
  if (input.documentsInbox && !offersDocumentsInbox(input.kind, input.moduleKey)) return 'Only the Accounting module’s mailbox can be the Documents Inbox.';
  return null;
}
